/**
 * CandidateAssignments — recruiter-curated take-home / assignment
 * submissions attached to a candidate. Mirrors CandidatePortfolio's
 * shape (link or file, with title/description/recruiter_comment) but
 * lives under its own section so recruiters don't confuse portfolio
 * work with job-specific assignments. Rendered right below Portfolio
 * on the Overview tab.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, Upload, ExternalLink, FileText, Link as LinkIcon } from "lucide-react";

function shortDate(iso) {
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium" }); } catch { return ""; }
}

export default function CandidateAssignments({ candidateId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showLink, setShowLink] = useState(false);
    const [link, setLink] = useState({ url: "", title: "", description: "", recruiter_comment: "" });
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/candidates/${candidateId}/assignments`);
            setItems(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [candidateId]);

    const addLink = async () => {
        if (!link.url) return toast.error("URL required");
        try {
            await api.post(`/candidates/${candidateId}/assignments/link`, link);
            setLink({ url: "", title: "", description: "", recruiter_comment: "" });
            setShowLink(false);
            toast.success("Assignment link added");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const upload = async (f) => {
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/candidates/${candidateId}/assignments/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Assignment uploaded");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this assignment?")) return;
        try { await api.delete(`/candidates/${candidateId}/assignments/${id}`); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <section data-testid="candidate-assignments">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-ikb" />
                    <h3 className="font-display font-bold text-sm uppercase tracking-widest text-foreground">Assignments · {items.length}</h3>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setShowLink((v) => !v)} className="h-8 px-3 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-50 inline-flex items-center gap-1.5" data-testid="assign-add-link">
                        <LinkIcon className="w-3.5 h-3.5" /> Add link
                    </button>
                    <label className="h-8 px-3 border border-ikb bg-ikb text-white text-[10px] font-mono uppercase tracking-widest hover:bg-ikb/90 inline-flex items-center gap-1.5 cursor-pointer" data-testid="assign-add-file">
                        <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload file"}
                        <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} />
                    </label>
                </div>
            </div>

            {showLink && (
                <div className="border border-border bg-white p-3 mb-3 space-y-2" data-testid="assign-link-form">
                    <input type="url" placeholder="Assignment URL (e.g., GitHub PR, Figma link)" value={link.url} onChange={(e) => setLink({ ...link, url: e.target.value })} className="h-9 w-full border border-border bg-white text-sm px-2" />
                    <input type="text" placeholder="Title (optional)" value={link.title} onChange={(e) => setLink({ ...link, title: e.target.value })} className="h-9 w-full border border-border bg-white text-sm px-2" />
                    <textarea rows={2} placeholder="Recruiter comment (optional)" value={link.recruiter_comment} onChange={(e) => setLink({ ...link, recruiter_comment: e.target.value })} className="w-full border border-border bg-white text-sm px-2 py-1" />
                    <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => setShowLink(false)} className="h-8 px-3 border border-border text-[10px] font-mono uppercase tracking-widest">Cancel</button>
                        <button onClick={addLink} className="h-8 px-3 border border-ikb bg-ikb text-white text-[10px] font-mono uppercase tracking-widest" data-testid="assign-link-save">Save</button>
                    </div>
                </div>
            )}

            {loading ? <div className="text-xs text-muted-foreground">Loading…</div>
                : items.length === 0 ? <div className="border border-dashed border-border p-4 text-xs text-muted-foreground text-center">No assignments yet — add a link to a submitted take-home or upload the file.</div>
                : (
                    <ul className="space-y-2">
                        {items.map((it) => (
                            <li key={it.id} className="border border-border bg-white p-3 flex items-start gap-3" data-testid={`assign-item-${it.id}`}>
                                <div className="mt-0.5">
                                    {it.kind === "link" ? <LinkIcon className="w-4 h-4 text-ikb" /> : <FileText className="w-4 h-4 text-ikb" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <span className="font-medium text-sm truncate">{it.title || it.filename || it.url}</span>
                                        <span className="text-[10px] font-mono text-muted-foreground">{shortDate(it.added_at)} · {it.added_by_name}</span>
                                    </div>
                                    {it.description && <div className="text-xs text-muted-foreground mt-0.5">{it.description}</div>}
                                    {it.recruiter_comment && <div className="text-xs italic text-foreground/80 mt-1">"{it.recruiter_comment}"</div>}
                                    {it.kind === "link" && (
                                        <a href={it.url} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-ikb hover:underline inline-flex items-center gap-1 mt-1">
                                            {it.url} <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                    {it.kind === "file" && it.file_url && (
                                        <a href={it.file_url} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-ikb hover:underline inline-flex items-center gap-1 mt-1">
                                            Download {it.filename} <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                </div>
                                <button onClick={() => del(it.id)} className="w-7 h-7 border border-border hover:bg-rose-50 hover:border-rose-300 inline-flex items-center justify-center" data-testid={`assign-delete-${it.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
        </section>
    );
}
